import React, { useState } from 'react';
import { useMessageBox } from './MessageBox';

interface VerificationFormProps {
  email: string;
  /** Custom verification handler (for extension/main app) */
  onVerify?: (code: string) => Promise<void>;
  /** Custom resend code handler (for extension/main app) */
  onResendCode?: () => Promise<void>;
  /** Loading state from parent component (for extension) */
  isLoading?: boolean;
}

export const VerificationForm: React.FC<VerificationFormProps> = ({ 
  email, 
  onVerify, 
  onResendCode,
  isLoading: parentIsLoading = false,
}) => {
  const { showMessageBox } = useMessageBox();
  
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loading = parentIsLoading || isLoading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!verificationCode.trim()) {
      return;
    }

    setIsLoading(true);

    try {
      // Use custom handler if provided (extension), otherwise throw error
      // Main app should handle verification through AuthForm/AuthContext
      if (onVerify) {
        await onVerify(verificationCode);
      } else {
        throw new Error('No verification handler available. Please use the registration form.');
      }
    } catch (err: any) {
      console.error('Verification failed:', err);
      let errorMessage = err.message || 'Verification failed. Please check your code and try again.';
      
      // Handle Stripe-related errors
      if (err.errorCode === 'STRIPE_CUSTOMER_CREATION_FAILED' || 
          errorMessage.includes('Stripe') || 
          errorMessage.includes('STRIPE')) {
        errorMessage = (
          'Account verification failed due to a billing system error. ' +
          'Please contact support if this issue persists.'
        );
      }
      
      setError(errorMessage);
      
      showMessageBox({
        type: 'error',
        title: 'Verification Error',
        message: errorMessage,
        buttons: ['ok'],
        onButtonClick: () => {},
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (onResendCode) {
      try {
        setIsLoading(true);
        await onResendCode();
      } catch (err: any) {
        const errorMessage = err.message || 'Failed to resend verification code';
        setError(errorMessage);
        showMessageBox({
          type: 'error',
          title: 'Error',
          message: errorMessage,
          buttons: ['ok'],
          onButtonClick: () => {},
        });
      } finally {
        setIsLoading(false);
      }
    } else {
      // Main app - show message that resend is not available
      showMessageBox({
        type: 'info',
        title: 'Resend Code',
        message: 'Please use the registration form to resend the verification code.',
        buttons: ['ok'],
        onButtonClick: () => {},
      });
    }
  };

  return (
    <div className="verification-form">
      <h2>Verify Your Email</h2>
      <p>We've sent a verification code to <strong>{email}</strong></p>
      <p>Please check your email and enter the verification code below:</p>
      <p className="verification-form-hint">
        Can't find it? Check your <strong>spam or junk</strong> folder, then try &quot;Resend Code&quot; below.
      </p>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="verificationCode">Verification Code:</label>
          <input
            type="text"
            id="verificationCode"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            placeholder="Enter verification code"
            required
            disabled={loading || isLoading}
          />
        </div>
        
        {error && (
          <div className="error-message" role="alert">
            {error}
          </div>
        )}
        
        <button 
          type="submit" 
          className="auth-button"
          disabled={loading || isLoading || !verificationCode.trim()}
        >
          {loading || isLoading ? 'Verifying...' : 'Verify Email'}
        </button>
        
        {onResendCode && (
          <button 
            type="button" 
            onClick={handleResendCode}
            disabled={loading || isLoading}
            className="verification-resend-button"
          >
            Resend Code
          </button>
        )}
      </form>
    </div>
  );
};
